import type {
    AbstractAnalyticsDispatcher,
    OverridableActionsTypes,
    OverridableBusinessEventTypes,
} from "@asurion-core/eventualize";
import type { Logger } from "@expert/logging";

import { removeMdnFormatting } from "@expert/common-ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { DefaultPayload } from "../api";
import type { VerizonReplacementDevice } from "../api/prepareVerizonReplacement";
import type { ActivationFlowType } from "../api/verifyCustomer";
import type { VerifyVerizonCustomerResult } from "../api/verifyVerizonCustomer";
import type { GuideActionOption, GuideOption } from "../logic-builder";
import type { SessionMetadata } from "../store";
import type { GuideSessionContext } from "../types";
import type {
    ActivationAction,
    ActivationActionInput,
    ActivationActionResult,
    DeviceActivationCustomerState,
} from "./types";

import { activateDevice, verifyCustomer } from "../api";
import { generatePasscode } from "../api/generatePasscode";
import { generatePasscodeForVerification } from "../api/generatePasscodeForVerification";
import { prepareVerizonReplacement } from "../api/prepareVerizonReplacement";
import { verifyPasscode } from "../api/verifyPasscode";
import { verifyPasscodeForVerification } from "../api/verifyPasscodeForVerification";
import { verifyVerizonCustomer } from "../api/verifyVerizonCustomer";
import { encryptVerizonPin, preloadVerizonPie, VerizonPieUnavailableError } from "../api/verizonPinEncryption";
import {
    type BlockedActivation,
    buildActivationInputSignature,
    buildVerizonActivationInputSignature,
    isRetryBlocked,
    nextBlockedActivation,
} from "../components/activationRetryBlock";
import { resolveCustomerVerification } from "../components/customerVerificationResult";
import {
    EMPTY_DEVICE_ACTIVATION_CUSTOMER_STATE,
    selectDeviceActivationCustomerState,
    selectExtraAuthorizedToken,
    selectGuideSession,
    selectVerifiedCustomerToken,
    selectVerizonReplacementToken,
    useGuideStore,
} from "../store";

type Dispatcher = AbstractAnalyticsDispatcher<OverridableBusinessEventTypes, OverridableActionsTypes>;

/**
 * Customer-state fields a guide step may fill from its text input via the `setCustomerState` action
 * (the option's `value` names the field). Anything else is ignored, so an authored step can never
 * write, and so persist, a PIN or passcode.
 */
const INPUT_CUSTOMER_FIELDS = ["iccid", "imei", "phoneNumber"] as const;
type InputCustomerField = (typeof INPUT_CUSTOMER_FIELDS)[number];
const isInputCustomerField = (field: string): field is InputCustomerField =>
    (INPUT_CUSTOMER_FIELDS as readonly string[]).includes(field);

const VALID_MDN = /^\d{10}$/;

/** Dropdown actions whose options come from verification results rather than the authored flow. */
const DYNAMIC_DROPDOWN_ACTIONS: ReadonlySet<string> = new Set<ActivationAction>(["generatePasscode", "selectClaim"]);

/**
 * How a Verizon PIN verification ended, as reported on `VerifyVerizonCustomer`: the backend's outcome, or
 * `ENCRYPTION_UNAVAILABLE` when the PIN could not be encrypted, so nothing was sent.
 */
type VerizonPinOutcome = VerifyVerizonCustomerResult["outcome"] | "ENCRYPTION_UNAVAILABLE";

/**
 * The `verifyVerizonPin` step's branches. Only a wrong PIN is retried; a locked PIN goes to ACSS, and every
 * failure goes to `failure`, the manual flow, as `prepareVerizonReplacement`'s does.
 */
const VERIZON_PIN_NEXT_STEP: Record<VerizonPinOutcome, string> = {
    VERIFIED: "success",
    INCORRECT_PIN: "incorrectPin",
    MAX_ATTEMPTS: "maxAttempts",
    REQUEST_FAILED: "failure",
    ENCRYPTION_UNAVAILABLE: "failure",
};

export const MISSING_MDN_NOTICE =
    "We lost the customer's phone number. Go back to the phone number step and re-enter it, then try again.";
export const INVALID_MDN_NOTICE =
    "The customer's phone number isn't a valid 10-digit number. Go back to the phone number step and correct it, then try again.";
export const MISSING_VERIFICATION_NOTICE =
    "We lost the customer's verification. Go back and verify the customer again, then try again.";

export interface UseDeviceActivationArgs {
    actions: GuideActionOption[];
    currentStep: string;
    dispatcher: Dispatcher;
    extraData: Record<string, unknown>;
    logger: Logger;
    session: GuideSessionContext;
    sessionMetadata: SessionMetadata;
}

export interface UseDeviceActivation {
    assistContext: { activationFlowType?: ActivationFlowType; make?: string; model?: string };
    blockedReason?: string;
    deviceLabel: string;
    dropdownOptions: (authored: GuideOption[], dropdownAction?: string) => GuideOption[];
    failureReason?: string;
    handleAction: (action: ActivationAction, input: ActivationActionInput) => Promise<ActivationActionResult>;
    isActivationBlocked: boolean;
    isActivationSubmitStep: boolean;
    isVerizon: boolean;
    /** Set when an action refused to call the backend over missing guide state; cleared on step change. */
    recoveryNotice?: string;
    reset: () => void;
    shouldUseAlternateText: boolean;
    /** Verizon only: the replacement device to confirm with the customer before activating. */
    verizonReplacement?: VerizonReplacementDevice;
}

export function useDeviceActivation({
    actions,
    currentStep,
    dispatcher,
    extraData,
    logger,
    session,
    sessionMetadata,
}: UseDeviceActivationArgs): UseDeviceActivation {
    const {
        apiBaseUrl,
        callSid,
        clientChannelId,
        isProdMode = false,
        isVerizonDeviceActivationApiEnabled,
        partner,
        sessionId,
        ssoAccessToken,
        taskMdn,
    } = session;
    const isVerizon = partner === "verizon";
    const isVerizonApiEnabled = isVerizon && !!isVerizonDeviceActivationApiEnabled;

    // PIE is Verizon's script, so it is fetched only once a Verizon expert with the API enabled reaches the
    // PIN step, never on page load.
    const isVerizonPinStep =
        isVerizonApiEnabled && actions.some((option) => option.customAction === "verifyVerizonPin");
    useEffect(() => {
        if (isVerizonPinStep) preloadVerizonPie(isProdMode);
    }, [isVerizonPinStep, isProdMode]);

    // Customer state and verification tokens live in the session-keyed guide store, not component state:
    // the card unmounts whenever the guide closes (e.g. a Workspace V2 tab change) or the latest Simplr
    // message isn't a text step, while the Simplr room resumes at the step the expert left. Scoped to the
    // current room so an error-state restart (new room, first step) starts clean.
    const errorStateSessionId = useGuideStore((state) => selectGuideSession(state, sessionId).errorStateSessionId);
    const roomKey = errorStateSessionId || sessionId;
    const customerState = useGuideStore((state) => selectDeviceActivationCustomerState(state, sessionId, roomKey));
    const verifiedCustomerToken = useGuideStore((state) => selectVerifiedCustomerToken(state, sessionId, roomKey));
    const extraAuthorizedToken = useGuideStore((state) => selectExtraAuthorizedToken(state, sessionId, roomKey));
    const verizonReplacementToken = useGuideStore((state) => selectVerizonReplacementToken(state, sessionId, roomKey));
    // Whether this card mounted onto state an earlier mount stored (tab switch, remount, page reload).
    // Logged by the missing-MDN guard to tell a rehydration gap from a card that never had the MDN.
    // Tokens-only slices keep the empty sentinel as their customer state, so identity means "no answers".
    const hasStoredCustomerState = customerState !== EMPTY_DEVICE_ACTIVATION_CUSTOMER_STATE;
    const [mountedWithStoredState] = useState(hasStoredCustomerState);
    const [recoveryNotice, setRecoveryNotice] = useState<string | undefined>();

    // Writes re-check the room at write time: an action captures `roomKey` when it starts, and if the room
    // changed while its request was in flight (error-state restart), its result belongs to the old room.
    // It is dropped rather than written under the new key, whose guide starts over from the first step.
    const isCurrentRoom = () => {
        const currentErrorStateSessionId = selectGuideSession(useGuideStore.getState(), sessionId).errorStateSessionId;
        const currentRoomKey = currentErrorStateSessionId || sessionId;
        if (currentRoomKey === roomKey) return true;
        logger.warn(
            { sessionId, roomKey, currentRoomKey },
            "Solve Guide | Device activation | Room changed mid-request, dropping stale result",
        );
        return false;
    };
    const setCustomerState = (patch: Partial<DeviceActivationCustomerState>) => {
        if (isCurrentRoom()) useGuideStore.getState().setDeviceActivationCustomerState(sessionId, roomKey, patch);
    };
    const setVerifiedCustomerToken = (token: string) => {
        if (isCurrentRoom()) {
            useGuideStore.getState().setDeviceActivationTokens(sessionId, roomKey, { verifiedCustomerToken: token });
        }
    };
    const setExtraAuthorizedToken = (token: string) => {
        if (isCurrentRoom()) {
            useGuideStore.getState().setDeviceActivationTokens(sessionId, roomKey, { extraAuthorizedToken: token });
        }
    };
    const setVerizonReplacementToken = (token: string) => {
        if (isCurrentRoom()) {
            useGuideStore.getState().setDeviceActivationTokens(sessionId, roomKey, { verizonReplacementToken: token });
        }
    };
    // A failed activation stashes its reason in this ref (not state) so it does NOT render on the
    // activate screen where the button was clicked. It's promoted to the visible state only once we
    // navigate to the next step (the failure screen), and cleared on any later step change — handled
    // during render below.
    const pendingFailureReason = useRef<string | undefined>(undefined);
    const [failureReason, setFailureReason] = useState<string | undefined>();
    const [prevStep, setPrevStep] = useState(currentStep);
    const [blockedActivation, setBlockedActivation] = useState<BlockedActivation | undefined>();
    // The session this instance's retry block belongs to — lets us drop the block if the same GuideCard
    // instance is reused for a different session (multi-session chat without a per-chat remount key).
    const [blockSessionId, setBlockSessionId] = useState(sessionId);

    // On every step change, promote a pending failure reason (set by a failed activation on the
    // previous step) onto this step, otherwise clear it. Done during render so the reason only ever
    // appears on the failure step we navigate to — never on the activate screen or any later screen.
    if (currentStep !== prevStep) {
        setPrevStep(currentStep);
        const nextReason = pendingFailureReason.current;
        pendingFailureReason.current = undefined;
        if (nextReason !== failureReason) setFailureReason(nextReason);
        if (recoveryNotice) setRecoveryNotice(undefined);
    }

    // If this instance is reused for a different session (a chat layout without a per-chat remount key),
    // drop the retry block so one customer's block can't disable Activate for the next. Customer state and
    // tokens need no reset here: they are read from the store under the new session's key.
    if (sessionId !== blockSessionId) {
        setBlockSessionId(sessionId);
        setBlockedActivation(undefined);
        setRecoveryNotice(undefined);
    }

    // Verizon activates with the token minted for the looked-up device; its PIN token only unlocks the lookup.
    const activationToken = isVerizon ? verizonReplacementToken : extraAuthorizedToken || verifiedCustomerToken;

    // Fingerprint of the exact inputs sent to `activateDevice`. Used to decide whether a prior rejection
    // still applies: if the current inputs match the rejected ones, retrying would hit the same AT&T
    // business rule, so the Activate button stays disabled until an answer actually changes. Verizon keys
    // on the looked-up device instead: its token is re-minted by every lookup, even for the same device.
    const activationInputSignature = useMemo(
        () =>
            isVerizon
                ? buildVerizonActivationInputSignature({
                      activationFlowType: customerState.activationFlowType,
                      device: customerState.verizonReplacement,
                  })
                : buildActivationInputSignature({
                      activationFlowType: customerState.activationFlowType,
                      claimId: customerState.selectedClaimId,
                      imei: customerState.imei,
                      iccid: customerState.iccid,
                      token: activationToken,
                  }),
        [customerState, activationToken, isVerizon],
    );

    const defaultPayload: DefaultPayload = { callSid: callSid ?? "", clientChannelId, partner, sessionId };

    // Strips formatting and a +1 / leading 1 country code, so "1 (555) 123-4567" becomes "5551234567".
    const normalizeMdn = useCallback((mdn: string) => {
        const digits = removeMdnFormatting(mdn.replace("+1", ""));
        return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    }, []);
    const customerMdn = () => normalizeMdn(customerState.phoneNumber);

    /**
     * Guard for every request that sends the customer MDN. An empty or malformed MDN makes Layer7 reject
     * the call ("Missing mandatory parameters") and the backend surface it as a 500, so don't send it:
     * keep the expert on this step with a notice to re-enter the number. Logs lengths only, never the MDN.
     */
    const hasValidCustomerMdn = (action: ActivationAction) => {
        const mdn = customerMdn();
        if (VALID_MDN.test(mdn)) return true;
        logger.warn(
            {
                ...extraData,
                action,
                sessionId,
                roomKey,
                mdnLength: mdn.length,
                mountedWithStoredState,
                hasStoredCustomerState,
            },
            "Solve Guide | Device activation | Missing or invalid customer MDN, skipping request",
        );
        setRecoveryNotice(mdn ? INVALID_MDN_NOTICE : MISSING_MDN_NOTICE);
        return false;
    };

    /**
     * Guard for every request that needs a customer verification token. Tokens are in memory only, and
     * without one the backend can only reject the request, so keep the expert on this step instead.
     */
    const hasVerificationToken = (action: ActivationAction, token: string) => {
        if (token) return true;
        logger.warn(
            { ...extraData, action, sessionId, roomKey, mountedWithStoredState, hasStoredCustomerState },
            "Solve Guide | Device activation | Missing verification token, skipping request",
        );
        setRecoveryNotice(MISSING_VERIFICATION_NOTICE);
        return false;
    };

    const track = <TResult>(
        event: string,
        result: TResult,
        wasSuccessful: boolean,
        props?: Record<string, unknown>,
        skipOpenSearch = false,
    ) => {
        const target = skipOpenSearch ? dispatcher.withMeta({ SkipOpenSearch: true }) : dispatcher;
        void target.dispatchBusinessEvent(event, { wasSuccessful, ...extraData, ...props });
        return result;
    };

    const verifyPin = async (inputValue: string) => {
        const payload = { ...defaultPayload, mdn: customerMdn(), pin: inputValue };
        const result = await verifyCustomer(ssoAccessToken, payload, logger, apiBaseUrl);
        return track("VerifyCustomer", result, !!result.token);
    };

    const sendVerificationPasscode = async (targetMdn: string) => {
        const payload = { ...defaultPayload, mdn: customerMdn(), targetMdn: normalizeMdn(targetMdn) };
        const result = await generatePasscodeForVerification(ssoAccessToken, payload, logger, apiBaseUrl);
        return track("GeneratePasscodeForVerification", result, result.success);
    };

    const verifyVerificationPasscode = async (passcode: string) => {
        const payload = {
            ...defaultPayload,
            mdn: customerMdn(),
            passcode,
            transactionId: customerState.transactionId,
        };
        const result = await verifyPasscodeForVerification(ssoAccessToken, payload, logger, apiBaseUrl);
        return track("VerifyPasscodeForVerification", result, !!result.token);
    };

    const sendPasscode = async (selectedMdn: string) => {
        const mdn = normalizeMdn(selectedMdn);
        const payload = { ...defaultPayload, mdn };
        const result = await generatePasscode(ssoAccessToken, payload, verifiedCustomerToken, logger, apiBaseUrl);
        return track("GeneratePasscode", result, result.success, { mdn }, /* contains PII */ true);
    };

    const confirmPasscode = async (passcode: string) => {
        const payload = { ...defaultPayload, passcode, transactionId: customerState.transactionId };
        const result = await verifyPasscode(ssoAccessToken, payload, verifiedCustomerToken, logger, apiBaseUrl);
        return track("VerifyPasscode", result, !!result.token);
    };

    /**
     * Encrypts the PIN with Verizon's PIE, then verifies it. The plain PIN goes no further than
     * `encryptVerizonPin`: the request carries only the ciphertext, and neither logs nor analytics see it.
     */
    const checkVerizonPin = async (pin: string): Promise<VerifyVerizonCustomerResult | undefined> => {
        let encrypted;
        try {
            encrypted = await encryptVerizonPin(pin, isProdMode);
        } catch (error) {
            logger.warn(
                {
                    ...extraData,
                    verizonPieFailure: error instanceof VerizonPieUnavailableError ? error.failure : "unknown",
                },
                "Solve Guide | Device activation | Could not encrypt the Verizon PIN, using the manual flow",
            );
            return undefined;
        }
        const payload = { ...defaultPayload, mdn: customerMdn(), ...encrypted };
        return verifyVerizonCustomer(ssoAccessToken, payload, logger, apiBaseUrl);
    };

    const verifyVerizonPin = async (pin: string) => {
        const result = await checkVerizonPin(pin);
        const outcome: VerizonPinOutcome = result?.outcome ?? "ENCRYPTION_UNAVAILABLE";
        const token = result?.outcome === "VERIFIED" ? result.token : "";
        const errorCode = result?.outcome === "REQUEST_FAILED" ? result.errorCode || undefined : undefined;
        // Outcome codes only: never the PIN, its ciphertext or the MDN.
        return track("VerifyVerizonCustomer", { outcome, token }, outcome === "VERIFIED", {
            partner,
            outcome,
            ...(errorCode && { errorCode }),
        });
    };

    const lookUpVerizonReplacement = async () => {
        const payload = { ...defaultPayload, token: verifiedCustomerToken };
        const result = await prepareVerizonReplacement(ssoAccessToken, payload, logger, apiBaseUrl);
        const eligibilityCode = result.eligible ? undefined : result.code || result.errorCode || undefined;
        return track("PrepareVerizonReplacement", result, result.eligible, { partner, eligibilityCode });
    };

    const selectClaim = (claimId: string) => {
        const claimSummary = customerState.claimSummaries.find((cl) => cl.claimId === claimId);
        if (!claimSummary) return "selectClaimFailure";

        setCustomerState({ activationFlowType: "REPLACEMENT_DEVICE", selectedClaimId: claimId });
        void dispatcher.dispatchBusinessEvent("SelectClaim", {
            activationFlowType: "REPLACEMENT_DEVICE",
            ...claimSummary,
            ...extraData,
        });

        // non-PA4 claims require extra OTP authorization before activation
        return claimSummary.requiresExtraAuth && !extraAuthorizedToken ? "generatePasscode" : "activateReady";
    };

    const submitActivation = async () => {
        const { activationFlowType } = customerState;
        // Verizon's device and line travel only inside the token from `prepareVerizonReplacement`; the
        // backend rejects device data from the browser, so none is sent.
        const payload = isVerizon
            ? { ...defaultPayload, activationFlowType }
            : {
                  ...defaultPayload,
                  activationFlowType,
                  claimId: customerState.selectedClaimId,
                  ...(customerState.imei
                      ? { manualDeviceInfo: { imei: customerState.imei, iccid: customerState.iccid } }
                      : undefined),
              };
        const {
            wasSuccessful,
            failureReason: reason,
            alreadyActive,
            failureCode,
            isRetryable,
        } = await activateDevice(ssoAccessToken, payload, activationToken, logger, apiBaseUrl);

        if (!wasSuccessful) pendingFailureReason.current = reason;

        // A surfaced reason means AT&T rejected on a business rule (Layer7) — retrying the same inputs will
        // fail the same way, so block retry until an activation answer changes; success clears it; transient
        // failures (no reason) stay retryable. Verizon says so explicitly: only NOT_RETRYABLE blocks, a
        // REJECTED reason is shown but the expert may retry. See `nextBlockedActivation`.
        setBlockedActivation((prev) =>
            nextBlockedActivation(
                prev,
                { wasSuccessful, failureReason: reason, isRetryable },
                activationInputSignature,
            ),
        );

        // reasonSurfaced: whether the expert was shown an actionable AT&T reason on the failure step.
        // Lets us measure feature coverage (what % of failures surface a reason) and segment the
        // un-retryable failures this targets from transient ones for the retry/time-saved before/after.
        // NOTE: the raw reason text is intentionally NOT sent here — some AT&T messages embed the
        // customer MDN (PII), and analytics flows to EIP/EDP. Per-reason breakdown comes from the
        // backend logs (`err.aggregateErrors`) instead.
        const reasonSurfaced = !wasSuccessful && reason !== undefined;
        if (reasonSurfaced && isRetryable !== true) {
            // Measures how often we prevent an un-retryable retry (pairs with the retry-count before/after).
            // PII-safe: no raw reason text (some AT&T reasons embed the customer MDN) — same rule as
            // reasonSurfaced above.
            void dispatcher.dispatchBusinessEvent("ActivateRetryBlocked", {
                activationFlowType,
                partner,
                ...(failureCode && { failureCode }),
                ...extraData,
            });
        }
        // `alreadyActive` and `failureCode` are Verizon's bounded outcome codes, never free text.
        void dispatcher.dispatchBusinessEvent("ActivateDevice", {
            activationFlowType,
            wasSuccessful,
            reasonSurfaced,
            partner,
            ...(isVerizon && { alreadyActive, failureCode }),
            ...extraData,
        });
        return wasSuccessful;
    };

    const handlers: Record<
        ActivationAction,
        (input: ActivationActionInput) => ActivationActionResult | Promise<ActivationActionResult>
    > = {
        setCustomerState: ({ inputValue, option }) => {
            if (isInputCustomerField(option.value)) {
                setCustomerState({ [option.value]: inputValue });
                if (option.value === "phoneNumber") setRecoveryNotice(undefined);
            } else {
                logger.warn(
                    { ...extraData, field: option.value },
                    "Solve Guide | Device activation | setCustomerState for an unknown field, ignoring",
                );
            }
            return { status: "next", nextStep: "Continue" };
        },
        verifyPin: async ({ inputValue }) => {
            if (!hasValidCustomerMdn("verifyPin")) return { status: "recoveryNeeded" };
            const verification = resolveCustomerVerification(await verifyPin(inputValue));
            setCustomerState(verification.customerPatch);
            if (verification.token) setVerifiedCustomerToken(verification.token);
            return { status: "next", nextStep: verification.nextStep };
        },
        generatePasscodeForVerification: async ({ inputValue }) => {
            if (!hasValidCustomerMdn("generatePasscodeForVerification")) return { status: "recoveryNeeded" };
            const { success, transactionId } = await sendVerificationPasscode(inputValue);
            setCustomerState({ transactionId });
            return { status: "next", nextStep: success ? "success" : "failure" };
        },
        verifyPasscodeForVerification: async ({ inputValue }) => {
            if (!hasValidCustomerMdn("verifyPasscodeForVerification")) return { status: "recoveryNeeded" };
            const verification = resolveCustomerVerification(await verifyVerificationPasscode(inputValue));
            setCustomerState(verification.customerPatch);
            if (verification.token) setExtraAuthorizedToken(verification.token);
            return { status: "next", nextStep: verification.nextStep };
        },
        setActivationTypeToNew: () => {
            setCustomerState({ activationFlowType: "NEW_DEVICE" });
            return {
                status: "next",
                nextStep: extraAuthorizedToken ? undefined : "activateDifferentDeviceNeedsExtraAuth",
            };
        },
        generatePasscode: async ({ hasDropdown, selectedOption }) => {
            // Only demand a selection when the step actually offers one. Steps may fire this action from a
            // plain button to send the passcode to the account's own number, and an empty `mdn` is what the
            // pre-refactor code sent in that case. Unlike `selectClaim` (below), which is meaningless
            // without a claim, so it always requires the selection.
            if (hasDropdown && !selectedOption?.value) return { status: "needsDropdownSelection" };
            if (!hasVerificationToken("generatePasscode", verifiedCustomerToken)) return { status: "recoveryNeeded" };
            const { success, transactionId } = await sendPasscode(selectedOption?.value ?? "");
            setCustomerState({ transactionId });
            return { status: "next", nextStep: success ? "success" : "failure" };
        },
        verifyPasscode: async ({ inputValue }) => {
            if (!hasVerificationToken("verifyPasscode", verifiedCustomerToken)) return { status: "recoveryNeeded" };
            const { token } = await confirmPasscode(inputValue);
            if (token) setExtraAuthorizedToken(token);
            return { status: "next", nextStep: token ? "success" : "failure" };
        },
        verifyVerizonPin: async ({ inputValue }) => {
            // Flag off (or not Verizon): route to the authored failure step, which is the manual flow.
            if (!isVerizonApiEnabled) {
                logger.debug(
                    { ...extraData, partner },
                    "Solve Guide | Device activation | Verizon activation not enabled, using the manual flow",
                );
                return { status: "next", nextStep: "failure" };
            }
            if (!hasValidCustomerMdn("verifyVerizonPin")) return { status: "recoveryNeeded" };

            const { outcome, token } = await verifyVerizonPin(inputValue);
            // A new verification starts over: drop any device and tokens from an earlier one, so neither a
            // lookup nor an activation can run on a line this attempt did not verify.
            setCustomerState({ activationFlowType: undefined, verizonReplacement: undefined });
            setVerifiedCustomerToken(token);
            setVerizonReplacementToken("");
            return { status: "next", nextStep: VERIZON_PIN_NEXT_STEP[outcome] };
        },
        prepareVerizonReplacement: async () => {
            // Flag off (or not Verizon): route to the authored failure step, which is the manual flow.
            if (!isVerizonApiEnabled) {
                logger.debug(
                    { ...extraData, partner },
                    "Solve Guide | Device activation | Verizon activation not enabled, using the manual flow",
                );
                return { status: "next", nextStep: "failure" };
            }
            if (!hasVerificationToken("prepareVerizonReplacement", verifiedCustomerToken)) {
                return { status: "recoveryNeeded" };
            }

            const result = await lookUpVerizonReplacement();
            if (result.eligible) {
                const { make, model, eSim, lineLast4, claimDate, token } = result;
                setCustomerState({
                    activationFlowType: "REPLACEMENT_DEVICE",
                    verizonReplacement: { make, model, eSim, lineLast4, claimDate },
                });
                setVerizonReplacementToken(token);
                return { status: "next", nextStep: "success" };
            }

            // Drop a device and token from an earlier lookup so activation can never use them. The PIN
            // verification stands, so the expert can go back and run the lookup again.
            setCustomerState({ verizonReplacement: undefined });
            setVerizonReplacementToken("");
            return { status: "next", nextStep: result.code ? "noEligibleClaim" : "failure" };
        },
        selectClaim: ({ selectedOption }) => {
            if (!selectedOption?.value) return { status: "needsDropdownSelection" };
            return { status: "next", nextStep: selectClaim(selectedOption.value) };
        },
        activateDevice: async () => {
            if (!hasVerificationToken("activateDevice", activationToken)) return { status: "recoveryNeeded" };
            return { status: "next", nextStep: (await submitActivation()) ? "success" : "failure" };
        },
    };

    const deviceMake = typeof sessionMetadata.make === "string" ? sessionMetadata.make : undefined;
    const deviceModel = typeof sessionMetadata.model === "string" ? sessionMetadata.model : undefined;

    const dropdownOptions = useCallback(
        (authored: GuideOption[], dropdownAction?: string) => {
            // Flows may carry the dynamic customAction on one of the authored options (the button that
            // submits the selection) instead of on the dropdown's own action - AT&T's SelectClaim does.
            const dynamicAction =
                dropdownAction ??
                authored.find((option) => DYNAMIC_DROPDOWN_ACTIONS.has(option.customAction ?? ""))?.customAction;
            switch (dynamicAction) {
                case "generatePasscode":
                    return customerState.accountMdns.map((item) => ({ label: item.masked, value: item.value }));
                case "selectClaim":
                    return customerState.claimSummaries.map((item) => ({
                        label: `${item.deviceDescription} (${item.insuranceType})`,
                        value: item.claimId,
                    }));
                default:
                    return authored;
            }
        },
        [customerState.accountMdns, customerState.claimSummaries],
    );

    const reset = useCallback(() => setBlockedActivation(undefined), []);

    return useMemo(
        () => ({
            assistContext: {
                activationFlowType: customerState.activationFlowType,
                make: deviceMake,
                model: deviceModel,
            },
            blockedReason: blockedActivation?.reason,
            deviceLabel: [deviceMake, deviceModel].filter(Boolean).join(" ").trim(),
            dropdownOptions,
            failureReason,
            handleAction: async (action: ActivationAction, input: ActivationActionInput) => handlers[action](input),
            isActivationBlocked: isRetryBlocked(blockedActivation, activationInputSignature),
            isActivationSubmitStep: actions.some((option) => option.customAction === "activateDevice"),
            isVerizon,
            recoveryNotice,
            reset,
            shouldUseAlternateText:
                actions.some((option) => option.customAction === "activateDevice") &&
                normalizeMdn(customerState.phoneNumber) === normalizeMdn(taskMdn),
            verizonReplacement: customerState.verizonReplacement,
        }),
        [
            actions,
            activationInputSignature,
            blockedActivation,
            customerState,
            deviceMake,
            deviceModel,
            dropdownOptions,
            failureReason,
            handlers,
            isVerizon,
            recoveryNotice,
            reset,
            taskMdn,
        ],
    );
}
